import { BaseWebSocketClient } from './baseWebSocketClient.js'

document.addEventListener('alpine:init', () => {
  Alpine.data('studentAssignment', (config) => ({
    state: {
      assignment: {
        id: config.assignment_id,
        title: config.assignment_title,
      },
      student_assignment: {
        id: config.student_assignment_id,
        is_submitted: config.is_submitted === 'true',
        is_graded: config.is_graded === 'true',
      },
      node: {
        id: config.node_id,
      },
      feedback: {
        loading: true,
        content: '',
        updated_at: null,
        error: null,
        teacher: {
          id: null,
          designation: null,
          letter: null,
          color: null,
          photo_url: null,
        },
        score: null,
        max_points: null,
      },
      overall_feedback: {
        content: '',
        updated_at: null,
      },
    },
    websocketClient: null,

    init() {
      this.fetchFeedback()
      this.initializeWebSocket()
      this.setupTypingDetection()
    },

    async fetchFeedback() {
      try {
        this.state.feedback.loading = true
        this.state.feedback.error = null

        const response = await fetch(
          `/api/student-assignments/${this.state.student_assignment.id}/feedback/?node_id=${this.state.node.id}`
        )

        if (response.status === 403) {
          this.state.feedback.error = 'Permission denied'
          return
        }

        if (response.status === 404) {
          this.state.feedback.error = ''
          return
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.error) {
          this.state.feedback.error = data.error
          return
        }

        if (data.node) {
          this.state.feedback.content = data.node.feedback || ''
          this.state.feedback.updated_at = data.node.updated_at
            ? this.formatDate(data.node.updated_at)
            : null
          this.state.feedback.teacher = data.node.teacher || null
          this.state.feedback.score = data.node.score || null
          this.state.feedback.max_points = data.node.max_points || null
        }

        //overall feedback for potential future use
        if (data.overall_feedback && data.overall_feedback.content) {
          this.state.overall_feedback = {
            content: data.overall_feedback.content,
            updated_at: data.overall_feedback.updated_at
              ? this.formatDate(data.overall_feedback.updated_at)
              : null,
          }
        }
      } catch (error) {
        console.error('Error fetching feedback:', error)
        this.state.feedback.error = 'Failed to load feedback'
      } finally {
        this.state.feedback.loading = false
      }
    },

    initializeWebSocket() {
      this.websocketClient = new StudentWebSocketClient(
        this.state.assignment.id,
        this.state.student_assignment.id,
        this
      )
      window.studentWebSocket = this.websocketClient
    },
    setupTypingDetection() {
      // Add typing detection to text inputs
      document
        .querySelectorAll(
          'textarea, input[type="text"], div[contenteditable="true"]'
        )
        .forEach((input) => {
          // Detect when input is focused
          input.addEventListener('focus', () => {
            if (this.websocketClient) {
              this.websocketClient.startTyping(this.state.node.id)
            }
          })

          // Also detect actual typing
          input.addEventListener('input', () => {
            if (this.websocketClient) {
              this.websocketClient.startTyping(this.state.node.id)
            }
          })
        })
    },

    updateFeedbackFromWebSocket(data) {
      if (
        data.type === 'feedback_update' &&
        data.node_id === parseInt(this.state.node.id)
      ) {
        this.state.feedback.content = data.feedback
        this.state.feedback.updated_at = this.formatDate(
          new Date().toISOString()
        )
        // Update teacher info if provided in WebSocket update
        if (data.teacher) {
          this.state.feedback.teacher = data.teacher
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    },

    destroy() {
      if (this.websocketClient) {
        this.websocketClient.disconnect()
      }
    },
  }))
  // Student WebSocket client for real-time updates
  class StudentWebSocketClient extends BaseWebSocketClient {
    constructor(assignmentId, studentAssignmentId, alpineComponent = null) {
      super(assignmentId, alpineComponent)
      this.studentAssignmentId = studentAssignmentId
      this.typingTimeout = null
      this.isTyping = false
    }

    getWebSocketUrl() {
      return `/ws/assignment/${this.assignmentId}/`
    }

    onMessage(data) {
      if (!this.alpineComponent) return
      switch (data.type) {
        case 'feedback_update':
          this.alpineComponent.updateFeedbackFromWebSocket(data)
          break
        case 'pong':
          this.handlePong()
          break
      }
    }

    startTyping(nodeId) {
      if (
        !this.isTyping &&
        this.socket &&
        this.socket.readyState === WebSocket.OPEN
      ) {
        this.isTyping = true
        this.sendTypingActivity(nodeId)
      }
      clearTimeout(this.typingTimeout)
      this.typingTimeout = () => {
        document.activeElement.addEventListener('blur', () => {
          this.stopTyping()
        })
      }
      this.typingTimeout()
    }

    stopTyping() {
      if (
        this.isTyping &&
        this.socket &&
        this.socket.readyState === WebSocket.OPEN
      ) {
        this.isTyping = false
        this.socket.send(
          JSON.stringify({
            type: 'stop_typing',
            student_id: this.studentAssignmentId,
          })
        )
      }
    }

    sendTypingActivity(nodeId) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        const message = {
          type: 'typing',
          student_id: this.studentAssignmentId,
          node_id: nodeId,
          timestamp: Date.now(),
        }
        this.socket.send(JSON.stringify(message))
      }
    }
  }

  // Clean up on page unload
  window.addEventListener('beforeunload', function () {
    if (window.studentWebSocket) {
      window.studentWebSocket.disconnect()
    }
  })
})
